import styled from "styled-components";
import { frequencyOptions } from "src/constants/options";
import { SAVINGS } from "./savings";

/** Every 2 weeks — the plan most customers take. */
const POPULAR = 15;

/**
 * How often, as one segmented bar at the head of the date step's card.
 *
 * Real radio inputs under the labels, so arrow keys move between plans and a
 * screen reader hears one group of four — the look is the only custom part.
 *
 * The popular plan is marked the way a person would mark it: a loose loop of
 * ink around it and a note in hand (17 Sep redesign, after Kengo Works). It
 * replaced a floating arrow and "Most popular" label that sat above the row
 * and belonged to nothing. The loop marks popularity, not selection — the
 * blue fill says which plan is chosen — so the two never compete.
 */
export function PlanPicker({
  value,
  onChange,
}: {
  value: number | string;
  onChange: (value: number) => void;
}) {
  const chosen = Number(value) || 0;
  return (
    <Bar role="radiogroup" aria-label="How often" id="occurance">
      {frequencyOptions.map((option) => {
        const checked = chosen === option.value;
        const popular = option.value === POPULAR;
        return (
          <Option key={option.value} $checked={checked}>
            <input
              type="radio"
              name="occurance"
              value={option.value}
              checked={checked}
              onChange={() => onChange(option.value)}
            />
            <span className="name">{option.label}</span>
            <span className="sub">{option.tag ?? "Single visit"}</span>
            {popular ? <InkMark /> : null}
          </Option>
        );
      })}
    </Bar>
  );
}

/** The loop and the note. Decorative: the option's own label says what it is. */
function InkMark() {
  return (
    <>
      <svg className="ink loop" viewBox="0 0 220 92" preserveAspectRatio="none" aria-hidden="true">
        <path
          d="M34 11 C 92 -3, 188 1, 207 28 C 222 52, 176 86, 106 85 C 42 84, 5 66, 9 42 C 12 22, 44 9, 78 7"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <span className="note" aria-hidden="true">
        <svg className="ink arrow arrow-down" viewBox="0 0 40 34" width="40" height="34">
          <path d="M36 6 C 22 4, 10 12, 8 30 M2 22 L 8 31 L 15 24" />
        </svg>
        <svg className="ink arrow arrow-up" viewBox="0 0 34 30" width="34" height="30">
          <path d="M30 26 C 18 26, 8 18, 8 3 M2 10 L 8 2 L 15 9" />
        </svg>
        <span className="hand">our most popular</span>
      </span>
    </>
  );
}

/*
  No tray on a phone.

  The grey `#f2f4f9` tray cost 6px of padding on each side, and at two columns
  that 12px was the difference between four plans that touch and four that
  read as separate choices. Spending it on the gap costs the tiles nothing:
  12px comes back, 8px of it goes to the gap (6px → 14px) and the remaining
  4px returns to the tiles, which end up 2px wider each. Their new outlines
  are `inset` shadows, so they take no layout space at all.

  It also gives the ink loop around the popular plan room it never had: the
  loop is drawn at `left: -16px` and `calc(100% + 32px)` wide, so at a 6px gap
  it ran over the neighbouring tile.

  The tray stays on wider screens, where four tiles in one row are a segmented
  control and the tray is what groups them. At two rows it never was one.
*/
const Bar = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;
  margin-top: 30px;
  padding: 6px;
  border-radius: 14px;
  background: #f2f4f9;

  @media (max-width: 560px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
    margin-top: 8px;
    margin-bottom: 44px;
    padding: 0;
    background: none;
  }
`;

const Option = styled.label<{ $checked: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-height: 60px;
  padding: 8px 6px;
  border-radius: 10px;
  cursor: pointer;
  text-align: center;
  transition: background-color 140ms ease;
  background: ${(p) => (p.$checked ? "var(--blue-300)" : "transparent")};
  color: ${(p) => (p.$checked ? "#fff" : "#1f2430")};
  box-shadow: ${(p) => (p.$checked ? "0 1px 2px rgba(37, 62, 198, 0.25)" : "none")};

  &:hover {
    background: ${(p) => (p.$checked ? "var(--blue-300)" : "#e8ebf5")};
  }

  /*
    Without the tray, an unchosen plan has nothing saying it is a control — it
    was a transparent label that only the tray's grey made read as a tile. Its
    own hairline says it instead. Inset, so it costs no width, and only where
    the tray is gone.
  */
  @media (max-width: 560px) {
    background: ${(p) => (p.$checked ? "var(--blue-300)" : "#fff")};
    box-shadow: ${(p) =>
      p.$checked
        ? "0 1px 2px rgba(37, 62, 198, 0.25)"
        : "inset 0 0 0 1px #dfe3ec"};

    &:hover {
      background: ${(p) => (p.$checked ? "var(--blue-300)" : "#f6f7fa")};
    }
  }

  /* The input stays in the accessibility tree and takes focus; it just isn't drawn. */
  input {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
    margin: 0;
  }

  &:focus-within {
    outline: 2px solid var(--blue-300);
    outline-offset: 2px;
  }

  .name {
    font-size: 15px;
    font-weight: 500;
    line-height: 1.3;
  }

  .sub {
    font-size: 12px;
    font-weight: 500;
    color: ${(p) => (p.$checked ? "#cdd6ff" : "#6b7280")};
  }

  /* A saving reads in the funnel's saving green, except on the blue fill. */
  input[value="20"] ~ .sub,
  input[value="15"] ~ .sub,
  input[value="10"] ~ .sub {
    color: ${(p) => (p.$checked ? "#cdd6ff" : SAVINGS.ink)};
  }

  /* ---- The ink mark ---- */
  .ink path {
    fill: none;
    stroke: var(--blue-300);
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 720;
    stroke-dashoffset: 0;
    animation: ink-draw 1.2s cubic-bezier(0.6, 0.1, 0.3, 1) 0.35s backwards;
  }

  .loop {
    position: absolute;
    left: -16px;
    top: -15px;
    width: calc(100% + 32px);
    height: calc(100% + 30px);
    overflow: visible;
    pointer-events: none;
  }

  .loop path {
    stroke-width: 1.8;
  }

  .arrow path {
    stroke-width: 1.6;
  }

  .note {
    position: absolute;
    top: -50px;
    left: 58%;
    display: flex;
    align-items: flex-end;
    gap: 2px;
    white-space: nowrap;
    pointer-events: none;
    animation: ink-fade 0.5s ease-out 1.2s backwards;
  }

  .arrow {
    overflow: visible;
  }

  .arrow-up {
    display: none;
  }

  .hand {
    font-family: "Nanum Pen Script", cursive;
    font-size: 24px;
    line-height: 1;
    color: var(--blue-300);
    transform: rotate(-4deg);
    padding-bottom: 14px;
  }

  /*
    On a phone the bar is two rows, and the popular plan sits on the second —
    so the note hangs below the bar, arrow pointing up, instead of over the row
    above it.
  */
  @media (max-width: 560px) {
    .note {
      top: auto;
      bottom: -46px;
      left: 18px;
      align-items: flex-start;
    }

    .arrow-down {
      display: none;
    }

    .arrow-up {
      display: block;
    }

    .hand {
      font-size: 22px;
      transform: rotate(-3deg);
      padding: 14px 0 0;
    }
  }

  @keyframes ink-draw {
    from {
      stroke-dashoffset: 720;
    }
  }

  @keyframes ink-fade {
    from {
      opacity: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .ink path,
    .note {
      animation: none;
    }

    transition: none;
  }
`;
